import { components } from '@convergence/client';
import { makeAutoObservable } from 'mobx';
import { toast } from 'react-toastify';
import { getTinkerBoxById } from '../api/queries/tinkerBox';
import { registerShortcut } from '../hooks/useShortcut';
import { API_ENDPOINT } from '../lib/api';
import { WebSocketClient } from '../lib/WebSocketClient';
import { TinkerBoxConnectionStore } from './TinkerBoxConnectionStore';

export const HomeFocusSelection = {
  terminal: 'terminal',
  assistant: 'assistant'
} as const;

type TinkerBoxes = components['schemas']['GetTinkerBoxesResponse'];

export type HomeFocusSelectionType =
  (typeof HomeFocusSelection)[keyof typeof HomeFocusSelection];

type LabsResponse = components['schemas']['LabsResponse'];

const modes = {
  terminal: 'terminal',
  notebook: 'notebook'
} as const;
export type Mode = (typeof modes)[keyof typeof modes];

export class HomeStore {
  tinkerBoxConnection: TinkerBoxConnectionStore;
  initialized = false;
  socket = new WebSocketClient({
    url: API_ENDPOINT.replace('http', 'ws')
  });
  constructor() {
    makeAutoObservable(this);
    this.connectToTinkerbox = this.connectToTinkerbox.bind(this);
    this.setMode = this.setMode.bind(this);
    this.connectToTinkerbox = this.connectToTinkerbox.bind(this);
    this.configureShortcuts();
    this.tinkerBoxConnection = new TinkerBoxConnectionStore({
      socket: this.socket,
      shortcutsToIgnore: []
    });
    this.tinkerBoxConnection.onLogoutCallback = () =>
      this.connectToTinkerbox(null);

    this.initialized = true;
    const href = window.location.href;
    const currentUrl = new URL(href);
    const searchParams = new URLSearchParams(currentUrl.search);
    // const panel = searchParams.get('panel');
    // if (panel) {
    //   this.setSelectedSidePanelView(panel as ViewType);
    // }

    const mode = searchParams.get('mode');
    if (mode) {
      this.setMode(mode as Mode);
    }
    const lab = searchParams.get('lab');
    if (lab) {
      this.setSelectedLabId(parseInt(lab));
    }
    const notebook = searchParams.get('nb');
    if (notebook) {
      this.setSelectedNotebookTitle(notebook);
    }
    const tinkerbox = searchParams.get('tinkerbox');
    if (tinkerbox) {
      this.connectToTinkerbox(parseInt(tinkerbox));
    }
  }

  // async init() {
  //   const href = window.location.href;
  //   const currentUrl = new URL(href);
  //   const searchParams = new URLSearchParams(currentUrl.search);
  //   const panel = searchParams.get('panel');
  //   if (panel) {
  //     this.setSelectedSidePanelView(panel as ViewType);
  //   }
  //
  //   const lab = searchParams.get('lab');
  //   if (lab) {
  //     this.setSelectedLabId(parseInt(lab));
  //   }
  //
  //   const tinkerbox = searchParams.get('tinkerbox');
  //   if (tinkerbox) {
  //     this.connectToTinkerbox(parseInt(tinkerbox));
  //   }
  //   this.initialized = true;
  // }

  tinkerBoxId: null | number = null;
  get tinkerBox() {
    console.log(JSON.parse(JSON.stringify(this.tinkerboxes)));
    return this.tinkerboxes?.tinkerboxes?.find(
      (tinkerBox) => tinkerBox.id === this.tinkerBoxId
    );
  }

  selectedMode: Mode = 'terminal';
  setMode(mode: Mode) {
    this.selectedMode = mode;
    this.replaceUrlParam('mode', mode);
  }

  async connectToTinkerBoxWhenAvailable(id: number) {
    this.tinkerBoxConnection.connectionState = 'connecting';
    const timeout = new Date(new Date().getTime() + 2 * 60 * 1000); // 2 minutes from now
    while (true) {
      console.log(new Date(), timeout);
      if (new Date() > timeout) {
        toast.error('Timeout waiting for Tinkerbox to be running');
        console.error('Timeout waiting for Tinkerbox to be running');
        break;
      }
      console.log('Checking if Tinkerbox is running...');
      try {
        const tinkerbox = await getTinkerBoxById(id);
        if (tinkerbox && tinkerbox.status === 'running') {
          console.log('Tinkerbox is running, connecting...');
          setTimeout(() => {
            this.connectToTinkerbox(id);
          }, 3000);
          break;
        }
      } catch (error) {
        console.error('Error fetching Tinkerbox:', error);
        await new Promise((resolve) => setTimeout(resolve, 2000));
        continue;
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }

  // setTinkerBoxId
  connectToTinkerbox(id: null | number) {
    this.tinkerBoxId = id;
    this.replaceUrlParam('tinkerbox', id?.toString());
    if (id) {
      console.log('Connecting to Tinkerbox with ID:', id);
      const tinkerBox = this.tinkerBox;
      if (tinkerBox) {
        if (tinkerBox.status !== 'running') {
          console.warn('Tinkerbox is not running, cannot connect');
          this.tinkerBoxId = null;
        } else {
          this.tinkerBoxConnection.setTinkerBox({
            id: tinkerBox.id,
            name: tinkerBox.name
          });
          this.tinkerBoxConnection.onLogoutCallback = () =>
            this.connectToTinkerbox(null);
        }
      }
    } else {
      console.log('No Tinkerbox ID provided, disconnecting');
      this.tinkerBoxConnection.logout();
    }
  }

  assistantVisible = false;
  setAssistantVisible = (visible: boolean) => {
    this.assistantVisible = visible;
  };

  assistantEnabled = false;
  setAssistantEnabled = (enabled: boolean) => {
    this.assistantEnabled = enabled;
  };

  labs: LabsResponse['labs'] | null = null;
  tinkerboxes: TinkerBoxes | null = null;
  setLabs(labsQuery: LabsResponse['labs']) {
    this.labs = labsQuery;
    const name = this.tinkerBox?.name;
    const id = this.tinkerBox?.id;
    if (name && id) {
      this.tinkerBoxConnection.setTinkerBox({ name, id });
    }
  }

  setTinkerBoxes(tinkerboxes: TinkerBoxes) {
    this.tinkerboxes = tinkerboxes;
    if (this.tinkerBoxId !== null) {
      const tinkerBox = this.tinkerBox;
      console.log(
        'Updating Tinkerbox connection to:',
        JSON.stringify(tinkerBox)
      );
      if (tinkerBox && this.tinkerBox.status === 'running') {
        this.tinkerBoxConnection.setTinkerBox({
          id: tinkerBox.id,
          name: tinkerBox.name
        });
      } else {
        this.replaceUrlParam('tinkerbox', null);
        this.tinkerBoxId = null;
      }
    }
  }

  showSidePanel = true;
  setShowSidePanel = (visible: boolean) => {
    this.showSidePanel = visible;
  };

  selectedLabId: null | number = null;
  setSelectedLabId = (id: null | number) => {
    this.selectedLabId = id;
    this.replaceUrlParam('lab', id?.toString());
  };

  get selectedLab() {
    return this.labs?.find((lab) => lab.id === this.selectedLabId);
  }

  selectedNotebookTitle: null | string = null;
  setSelectedNotebookTitle(title: null | string) {
    console.log(title);
    this.selectedNotebookTitle = title;
    this.replaceUrlParam('nb', title);
  }

  focus: HomeFocusSelectionType = HomeFocusSelection.terminal;
  setFocus = (focus: HomeFocusSelectionType) => {
    this.focus = focus;
  };

  // selectedSidePanelView: ViewType = 'tinkerboxFiles';
  // setSelectedSidePanelView = (view: ViewType) => {
  //   this.selectedSidePanelView = view;
  //   this.replaceUrlParam('panel', view);
  // };

  replaceUrlParam(key: string, value: string | null | undefined) {
    const href = window.location.href;
    const currentUrl = new URL(href);
    const searchParams = new URLSearchParams(currentUrl.search);
    if (value === null || value === undefined) {
      searchParams.delete(key);
    } else {
      searchParams.set(key, value);
    }
    window.history.replaceState(
      {},
      '',
      `${currentUrl.pathname}?${searchParams.toString()}`
    );
  }

  private configureShortcuts() {
    registerShortcut(
      {
        code: 'KeyF',
        modifiers: {
          mac: ['meta'],
          windows: ['ctrl'],
          linux: ['ctrl']
        },
        description: 'Find in terminal'
      },
      () => {
        this.setShowSidePanel(!this.showSidePanel);
      }
    );
    registerShortcut(
      {
        code: 'KeyK',
        modifiers: {
          mac: ['meta'],
          windows: ['ctrl'],
          linux: ['ctrl']
        },
        description: 'Toggle side panel (left)'
      },
      () => {
        this.setShowSidePanel(!this.showSidePanel);
      }
    );

    registerShortcut(
      {
        code: 'KeyL',
        preventDefault: true,
        description: 'Focus the assistant',
        modifiers: ['ctrl']
      },
      () => {
        this.setFocus(HomeFocusSelection.assistant);
      }
    );

    registerShortcut(
      {
        code: 'KeyG',
        preventDefault: true,
        modifiers: {
          mac: ['meta'],
          windows: ['ctrl'],
          linux: ['ctrl']
        },
        description: 'Change lab'
      },
      () => {
        // handleLabSwitch()
      }
    );

    registerShortcut(
      {
        code: 'KeyE',
        preventDefault: true,
        modifiers: {
          mac: ['meta'],
          windows: ['ctrl'],
          linux: ['ctrl']
        },
        description: 'Hide assistant'
      },
      () => {
        if (!this.assistantEnabled) return;
        if (!this.assistantVisible) {
          this.setFocus(HomeFocusSelection.assistant);
        } else {
          this.setFocus(HomeFocusSelection.terminal);
        }
        this.setAssistantVisible(!this.assistantVisible);
      }
    );
  }
}

export const homeStore = new HomeStore();
